"use client";

import { usePostHog } from "posthog-js/react";
import { useEffect } from "react";
import { useSessionContext } from "../components/SupabaseProvider";
import {
  aliasSignupAnonymousId,
  captureSignedUpOnce,
  identifyPostHogUser,
} from "../helpers/posthogIdentify";
import { markHadSession } from "../helpers/signupFlowExperiment";

// Identifies the signed-in user with PostHog. Mounted by both provider trees:
// AuthRedirect in the app, and MarketingProviders so a visitor who signs up
// in the signup dialog on a marketing page is identified there too.
//
// `onlyIfAnonymous` (marketing pages): PostHog's distinct id persists across
// both trees, so a returning user is usually identified already, and calling
// identify() again would only re-send their properties (a `$set` and a flags
// reload) on every marketing pageview.
//
// Also, after identify: merging in the anonymous id the account signed up
// under (which may be another browser's), and a brand-new account's "Signed
// Up", so a signup counts the same whichever page or browser it finished on.
export const usePostHogIdentify = ({
  onlyIfAnonymous = false,
}: { onlyIfAnonymous?: boolean } = {}) => {
  const { isLoading, session } = useSessionContext();
  const posthog = usePostHog();

  useEffect(() => {
    if (isLoading || !session) return;
    markHadSession();
    if (!posthog) return;
    if (!onlyIfAnonymous || posthog.get_distinct_id() !== session.user.id) {
      identifyPostHogUser(posthog, session.user);
    }
    aliasSignupAnonymousId(posthog, session.user);
    captureSignedUpOnce(posthog, session.user);
  }, [posthog, session, isLoading, onlyIfAnonymous]);
};
